import { CSSProperties, FC } from 'react';
import { useRouter } from 'next/router';

import LandingHero from '../LandingHero';
import ProofStrip from '../ProofStrip';
import Endorsement from '../Endorsement';
import ProductDemo from '../ProductDemo';
import Countdown from '../Countdown';
import Faq from '../Faq';
import Footer from '../Footer';
import PaywallBlock from '@components/quiz/PaywallBlock';
import { INITIAL_QUIZ_STATE } from '@lib/page-utils/Quiz';
import { FUNNEL_FONT_VARIABLES } from '@lib/funnels/funnel-fonts';
import type { PaywallPlanKey, SanityLandingPage } from '@lib/sanity/types';

const PRICING_ANCHOR = 'pricing';
/** FFH brand cyan (primary-600); the accent fallback when no creator colour. */
const BRAND_ACCENT = '#009DE5';
// Mirrors the schema validation in sanity/schemaTypes/landingComponents/
// landingHero.ts; guards documents saved before validation existed or via the
// API, since a malformed value silently breaks colour-mixing and rings.
const HEX_COLOUR_PATTERN = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const PAYWALL_WIDTH = 'mx-auto w-full max-w-md md:max-w-2xl';
// Trim the funnel's tall bottom padding: the FAQ follows the paywall here, so
// the fixed CTA bar needs only modest clearance rather than a full dead gap.
const PAYWALL_SECTION = 'px-6 pb-16 pt-8';

const PLAN_KEY_SUFFIX = '_yearly';
const PAYWALL_PLAN_KEYS: PaywallPlanKey[] = [
  'starter_yearly',
  'pro_yearly',
  'ultra_yearly'
];

/** Accepts both the friendly alias (?plan=pro) and the full plan key. */
const resolvePreselectedPlan = (
  planParam?: string
): PaywallPlanKey | undefined => {
  if (!planParam) return undefined;
  const normalised = planParam.toLowerCase();
  const candidate = normalised.endsWith(PLAN_KEY_SUFFIX)
    ? normalised
    : `${normalised}${PLAN_KEY_SUFFIX}`;
  return PAYWALL_PLAN_KEYS.find((key) => key === candidate);
};

type LandingPageRendererProps = {
  landingPage: SanityLandingPage;
};

const LandingPageRenderer: FC<LandingPageRendererProps> = ({ landingPage }) => {
  const { hero, productDemo, countdown, paywall, faq } = landingPage;
  const { query } = useRouter();

  const isCreator = hero.variant === 'creator';
  // The accent re-skins eyebrows, highlights and creator rings per creator;
  // CTAs stay brand cyan for conversion consistency.
  const creatorAccent = isCreator ? hero.accentColour?.trim() : undefined;
  const accent =
    creatorAccent && HEX_COLOUR_PATTERN.test(creatorAccent)
      ? creatorAccent
      : BRAND_ACCENT;

  // Personalisation v1 is URL-param driven. ?plan= pre-selects a pricing tier.
  // ?arm= is reserved for attribution but is not yet written to Stripe
  // metadata; that is blocked on the attribution format sign-off (FFH-1299).
  const planParam = Array.isArray(query.plan) ? query.plan[0] : query.plan;
  const preselectedPlan = resolvePreselectedPlan(planParam);

  return (
    // The per-creator accent is set once as a CSS custom property; sections
    // reference var(--accent) so re-skinning a page is a one-line change.
    <div
      className={`funnel-fonts ${FUNNEL_FONT_VARIABLES} min-h-screen bg-white font-body`}
      style={{ '--accent': accent } as CSSProperties}
    >
      <LandingHero hero={hero} ctaHref={`#${PRICING_ANCHOR}`} />

      <ProofStrip
        trustpilotTemplateId={paywall.trustpilotTemplateId}
        trustpilotHeight={paywall.trustpilotHeight}
        trustpilotWidth={paywall.trustpilotWidth}
        asSeenOnLabel={paywall.asSeenOnLabel}
        asSeenOnItems={paywall.asSeenOnItems}
        asSeenOnMonochrome={paywall.asSeenOnMonochrome}
        asSeenOnLogoHeight={paywall.asSeenOnLogoHeight}
      />

      {isCreator && <Endorsement hero={hero} />}

      {productDemo && <ProductDemo productDemo={productDemo} />}

      {countdown?.deadline && <Countdown countdown={countdown} />}

      <div id={PRICING_ANCHOR} className="bg-landing-surface">
        <PaywallBlock
          {...paywall}
          quizState={INITIAL_QUIZ_STATE}
          templateKey={landingPage.slug.current}
          paywallContext={null}
          preselectedPlan={preselectedPlan}
          containerClassName={PAYWALL_WIDTH}
          sectionClassName={PAYWALL_SECTION}
          fallbackEyebrow="Choose your plan"
        />
      </div>

      {faq && <Faq faq={faq} />}

      <Footer />
    </div>
  );
};

export default LandingPageRenderer;
